// Dependencies
import { push } from 'connected-react-router';
import { Formik } from 'formik';
import Immutable from 'immutable';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Yup from 'yup';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import Paper from '@material-ui/core/Paper';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import LogoutIcon from '@material-ui/icons/ExitToApp';

import * as FormUtils from 'global/utils/form-utils';
import Fab from '@material-ui/core/Fab';
import axios from 'global/axios-instance';
import { updateSession } from 'global/actions/global-actions';
import { logout } from 'global/actions/global-actions';

const styles = theme => ({
    container: {
        maxWidth: '1200px',
        width: '100%',
        margin: '1rem auto',
        padding: '2rem',
        textAlign: 'center',
        position: 'relative'
    },
    formContainer: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    headerContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        flexDirection: 'column',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row'
        }
    },
    details: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            alignItems: 'center'
        }
    },
    formControl: {
        margin: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            width: '30%'
        }
    },
    errorContainer: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '350px'
    },
    buttonContainer: {
        textAlign: 'right',
        margin: '1rem',
        '& > :last-child': {
            marginLeft: '.5rem'
        }
    },
    logoutButton: {
        position: 'absolute',
        top: '1rem',
        right: '1rem'
    }
});

const QUESTIONNAIRE_FIELDS = [
    'employedRegion',
    'residenceRegion',
    'accessRegion'
];

const validationSchema = Yup.object().shape({
    employedRegion: Yup.string().required('Please select an employed region'),
    residenceRegion: Yup.string().required('Please select a residence region'),
    accessRegion: Yup.object().test(
        'at-least-one',
        'Please select at least one region for access',
        value => Object.values(value).some(v => v === true)
    )
});

function mapStateToProps(state) {
    return {
        user: state.session.user
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        push,
        logout,
        updateSession
    }, dispatch);
}

@withStyles(styles)
@connect(mapStateToProps, mapDispatchToProps)
class LoginRegionQuestionnaire extends React.PureComponent {
    constructor(props) {
        super(props);
    }

    state = {
        regions: Immutable.List()
    };

    static propTypes = {
        classes: PropTypes.object.isRequired,
        push: PropTypes.func.isRequired,
        updateSession: PropTypes.func.isRequired,
        logout: PropTypes.func.isRequired,
        location: PropTypes.object,
        user: PropTypes.object.isRequired
    };

    static defaultProps = {
        location: {}
    };

    async componentDidMount() {
        const regions = (await axios.get('/lookups/regions')).data;

        // Filter out the EU, they gotta use the country questionnaire
        const filteredRegions = regions.filter(region => region.awsRegion !== 'eu-central-1');

        this.setState({
            regions: Immutable.fromJS(filteredRegions)
        });
    }

    onSubmit = async (values) => {
        const { user: sessionUser } = this.props;
        const payload = FormUtils.cleanupEmptyStrings(_.pick(values, QUESTIONNAIRE_FIELDS));
        const result = await axios.put(`/auth/regions/${sessionUser.userId}`, payload);
        const { success, user } = result.data;

        // updateSession comes from global-actions.js
        await this.props.updateSession({ user });

        if (success) {
            return this.props.push('/');
        } else {
            return this.props.push('/no-regions');
        }
    };

    onLogout = () => {
        this.props.logout();
        window.location.href = '/';
    };

    back = () => {
        this.props.push('/questionnaire-landing');
    };

    render() {
        const { classes } = this.props;
        const { regions } = this.state;

        let initialValues = FormUtils.getInitialValues(QUESTIONNAIRE_FIELDS);
        initialValues.employedRegion = '';
        initialValues.residenceRegion = '';
        initialValues.accessRegion = {};

        regions.forEach(r => {
            const region = r.get('awsRegion');
            initialValues.accessRegion[region] = false;
        });

        return (<Paper className={classes.container}>
            <Fab size='medium' variant='extended' className={classes.logoutButton} onClick={this.onLogout}><LogoutIcon/>Logout</Fab>
            <h2>Please answer these questions to continue logging in.</h2>
            <Formik
                ref={this.form}
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                    this.onSubmit(values)
                        .then(() => setSubmitting(false));
                }}
                validationSchema={validationSchema}>
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting
                    /* and other goodies */
                }) => (
                    <form onSubmit={handleSubmit}>
                        <div className={classes.formContainer}>
                            <FormControl component='fieldset' className={classes.formControl}>
                                <FormLabel component='legend'>What region are you employed in?</FormLabel>
                                <RadioGroup name='employedRegion' value={values.employedRegion} onBlur={handleBlur} onChange={handleChange} >
                                    {regions.sortBy(r => r.get('awsRegion')).map(r =>
                                        <FormControlLabel
                                            key={r.get('awsRegion')}
                                            value={r.get('awsRegion')}
                                            control={ <Radio/> }
                                            label={r.get('regionName')}
                                        />
                                    )}
                                </RadioGroup>
                                {errors.employedRegion && touched.employedRegion &&
                                    <FormHelperText error>{errors.employedRegion}</FormHelperText>
                                }
                            </FormControl>
                            <FormControl component='fieldset' className={classes.formControl}>
                                <FormLabel component='legend'>What region do you live in?</FormLabel>
                                <RadioGroup name='residenceRegion' value={values.residenceRegion} onBlur={handleBlur} onChange={handleChange} >
                                    {regions.sortBy(r => r.get('awsRegion')).map(r =>
                                        <FormControlLabel
                                            key={r.get('awsRegion')}
                                            value={r.get('awsRegion')}
                                            control={ <Radio/> }
                                            label={r.get('regionName')}
                                        />
                                    )}
                                </RadioGroup>
                                {errors.residenceRegion && touched.residenceRegion &&
                                    <FormHelperText error>{errors.residenceRegion}</FormHelperText>
                                }
                            </FormControl>
                            <FormControl component='fieldset' className={classes.formControl}>
                                <FormLabel component='legend'>What region(s) do you need access to?</FormLabel>
                                <FormGroup>
                                    {regions.sortBy(r => r.get('awsRegion')).map(r =>
                                        <FormControlLabel
                                            key={r.get('awsRegion')}
                                            control={
                                                <Checkbox name={`accessRegion.${r.get('awsRegion')}`}
                                                    checked={values.accessRegion[r.get('awsRegion')]}
                                                    onChange={handleChange} onBlur={handleBlur}
                                                    classes={{ root: classes.denseCheckbox }} />
                                            }
                                            label={r.get('regionName')}
                                        />
                                    )}
                                </FormGroup>
                                {errors.accessRegion && touched.accessRegion &&
                                    <FormHelperText error>{errors.accessRegion}</FormHelperText>
                                }
                            </FormControl>
                        </div>
                        <div className={classes.buttonContainer} >
                            <Fab size='medium' variant='extended' onClick={this.back} disabled={isSubmitting} className={classes.button}>
                                <ArrowBackIcon />
                                        Back
                            </Fab>
                            <Fab color='primary' size='medium' variant='extended' onClick={handleSubmit} disabled={isSubmitting}>
                                <ArrowUpwardIcon />
                                        Submit
                            </Fab>
                        </div>
                    </form>
                )}
            </Formik>
        </Paper>
        );
    }
}

export default LoginRegionQuestionnaire;
